import { useEffect, useRef, useState } from 'react';
import '../styles/history.css';
import { Link } from 'react-router-dom';
import { formatDuration, formatNumber, type Exercise, type Workout } from '@jungles/shared';
import { api } from '../lib/api';
import { workoutHistoryTitle } from '../lib/workoutTitle';
import { ChevronIcon } from './icons';

export type HistoryFilter = 'all' | 'strength' | 'cardio';
export interface HistoryPage {
  items: Workout[];
  months: { month: string; count: number; volumeKg: number; durationMin: number }[];
  today: string;
  timezone: string;
  nextCursor: string | null;
}

export function historyDate(date: string, month = false) {
  // Diary dates are local calendar days, not UTC instants to be shifted again.
  return new Intl.DateTimeFormat('ru-RU', month
    ? { month: 'long', year: 'numeric', timeZone: 'UTC' }
    : { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'UTC' })
    .format(new Date(`${date.slice(0, month ? 7 : 10)}${month ? '-01' : ''}T12:00:00Z`));
}

export function HistoryRow({ workout, exercises, timezone, compact = false, onDelete, busy = false }: {
  workout: Workout; exercises: Exercise[] | null; timezone: string; compact?: boolean;
  onDelete?: () => void; busy?: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const suppressClick = useRef(false);
  const details = [];
  if (compact) details.push(historyDate(workout.date));
  else if (workout.startedAt) details.push(new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: timezone }).format(new Date(workout.startedAt)));
  if (workout.durationMin !== null) details.push(formatDuration(workout.durationMin));
  if (workout.volumeKg > 0) details.push(`${formatNumber(workout.volumeKg)} кг`);
  if (workout.distanceKm) details.push(`${formatNumber(workout.distanceKm)} км`);
  return <article data-history-row={workout.id} className={`history-row${compact ? ' compact' : ''}${revealed ? ' revealed' : ''}`}
    onTouchStart={e => { const t = e.touches[0]; touch.current = t ? { x: t.clientX, y: t.clientY } : null; suppressClick.current = false; }}
    onTouchEnd={e => { const t = e.changedTouches[0], start = touch.current; touch.current = null;
      if (!compact && t && start && Math.abs(t.clientX-start.x) > 50 && Math.abs(t.clientX-start.x) > Math.abs(t.clientY-start.y)*1.5) {
        setRevealed(t.clientX < start.x); suppressClick.current = true;
      }
    }}>
    <Link className="history-open" to={`/workouts/${workout.id}`} onClick={e => { if (suppressClick.current) { e.preventDefault(); suppressClick.current = false; } }}>
      <span className="wi"><img src={workout.type === 'activity' ? '/assets/ui/activity-cardio.webp' : workout.exercises[0]?.icon ?? '/assets/ui/tab-workouts.webp'} alt="" /></span>
      <span className="wn"><b>{workoutHistoryTitle(workout, exercises)}</b><small>{details.join(' · ')}</small></span>
      <ChevronIcon className="chev" />
    </Link>
    {!compact ? <div className="history-actions">
      <button type="button" className="history-delete" aria-label="Удалить запись" disabled={busy} onClick={onDelete}>Удалить</button>
    </div> : null}
  </article>;
}

export function useHistory(filter: HistoryFilter) {
  const [page, setPage] = useState<HistoryPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const generation = useRef(0);
  const inFlight = useRef(false);
  const reload = () => setVersion(v => v + 1);
  useEffect(() => {
    const request = ++generation.current;
    inFlight.current = true; setPage(null); setLoading(true); setError(null);
    api.get<HistoryPage>(`/api/workouts/history?filter=${filter}&limit=50`)
      .then(data => { if (generation.current === request) setPage(data); })
      .catch(err => { if (generation.current === request) setError(err instanceof Error ? err.message : 'Не удалось загрузить историю'); })
      .finally(() => { if (generation.current === request) { inFlight.current = false; setLoading(false); } });
    return () => { generation.current++; };
  }, [filter, version]);
  const more = async () => {
    if (!page?.nextCursor || inFlight.current) return;
    const request = generation.current;
    inFlight.current = true; setLoading(true); setError(null);
    try {
      const next = await api.get<HistoryPage>(`/api/workouts/history?filter=${filter}&limit=50&cursor=${encodeURIComponent(page.nextCursor)}`);
      if (request !== generation.current) return;
      setPage(current => current ? { ...next,
        items: [...new Map([...current.items, ...next.items].map(w => [w.id, w])).values()],
        months: [...new Map([...current.months, ...next.months].map(m => [m.month, m])).values()],
      } : next);
    } catch (err) { if (request === generation.current) setError(err instanceof Error ? err.message : 'Не удалось загрузить историю'); }
    finally { if (request === generation.current) { inFlight.current = false; setLoading(false); } }
  };
  return { page, loading, error, reload, more };
}
